import React from 'react';
import { Typography, Box, Grid, Card, Stack, Divider, Button as MuiButton, CircularProgress } from '@mui/material';
import { ArrowUpRight, ArrowDownRight, ArrowRight, Download, BarChart2, FileText, Zap, Heart, Rocket } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export default function Analytics() {
    const { posts, usage, isLoading } = useAppContext();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const totalPosts = posts.length;
    const creditsUsed = usage.generationsUsed;
    const efficiency = totalPosts > 0 ? Math.min(98.5, 82 + (totalPosts * 0.2)) : 0;

    // Group posts by type
    const carouselCount = posts.filter(p => p.postType === 'Carousel Post').length;
    const captionCount = posts.filter(p => p.postType === 'Caption Post').length;
    const otherCount = totalPosts - carouselCount - captionCount;

    const metrics = [
        { title: 'Total Generated', value: totalPosts.toLocaleString(), trend: '+12.5%', trendUp: true, icon: <FileText size={20} color="#4f46e5" /> },
        { title: 'Credits Consumed', value: creditsUsed.toLocaleString(), trend: '+8.2%', trendUp: false, icon: <Zap size={20} color="#f59e0b" /> },
        { title: 'Avg. Engagement', value: totalPosts > 0 ? '4.8%' : '0.0%', trend: '+0.4%', trendUp: true, icon: <Heart size={20} color="#ef4444" /> },
        { title: 'Content Efficiency', value: `${efficiency.toFixed(1)}%`, trend: '+2.1%', trendUp: true, icon: <Rocket size={20} color="#10b981" /> },
    ];

    // Chart data Simulation based on real post counts over 7 days
    const last7Days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        return d.toISOString().split('T')[0];
    });

    const weeklyData = last7Days.map(date => {
        return posts.filter(p => new Date(p.createdAt).toISOString().split('T')[0] === date).length;
    });

    const maxVal = Math.max(...weeklyData, 5);
    const chartPoints = weeklyData.map((val, i) => `${(i * 100) / 6},${80 - (val / maxVal) * 60}`).join(' ');

    const handleExport = () => {
        if (posts.length === 0) return;
        const headers = ['Topic', 'Type', 'Tone', 'Audience', 'Created At'];
        const csvContent = [
            headers.join(','),
            ...posts.map(p => [
                `"${p.topic}"`,
                p.postType,
                p.tone,
                `"${p.audience}"`,
                p.createdAt
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `analytics_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box>
            {/* Title & Export Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight={800} sx={{ color: '#111827', mb: 1, letterSpacing: '-0.02em' }}>
                        Analytics Overview
                    </Typography>
                    <Typography variant="body1" color="#64748b">
                        Track your performance across all channels
                    </Typography>
                </Box>
                <MuiButton
                    variant="outlined"
                    startIcon={<Download size={16} />}
                    onClick={handleExport}
                    disabled={posts.length === 0}
                    sx={{
                        color: '#111827',
                        borderColor: '#e2e8f0',
                        bgcolor: 'white',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2,
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                    }}
                >
                    Export Report
                </MuiButton>
            </Box>

            {/* Top 4 KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {metrics.map((m, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {m.icon}
                                </Box>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    {m.trendUp ? <ArrowUpRight size={16} color="#10b981" /> : <ArrowDownRight size={16} color="#ef4444" />}
                                    <Typography variant="caption" fontWeight={700} color={m.trendUp ? '#10b981' : '#ef4444'}>
                                        {m.trend}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Typography variant="caption" color="#64748b" fontWeight={600} display="block" gutterBottom>
                                {m.title}
                            </Typography>
                            <Typography variant="h5" fontWeight={800} color="#111827">
                                {m.value}
                            </Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Main Charts Area */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 4, height: '100%', minHeight: 400 }}>
                        <Typography variant="h6" fontWeight={700} color="#111827" mb={4}>
                            Post Generations Over Time
                        </Typography>

                        <Box sx={{ height: 260, width: '100%', position: 'relative', mt: 2 }}>
                            <svg width="100%" height="100%" viewBox="0 0 100 80" preserveAspectRatio="none">
                                <defs>
                                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.2" />
                                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d={`M 0,80 L ${chartPoints} L 100,80 Z`}
                                    fill="url(#chartGradient)"
                                />
                                <polyline
                                    fill="none"
                                    stroke="#4f46e5"
                                    strokeWidth="2"
                                    points={chartPoints}
                                />
                            </svg>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                {last7Days.map((date, i) => (
                                    <Typography key={i} variant="caption" color="#94a3b8" fontWeight={600}>
                                        {new Date(date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 4 }}>
                        <Typography variant="h6" fontWeight={700} color="#111827" mb={3}>
                            Content Type Mix
                        </Typography>

                        <Stack spacing={3}>
                            <DistributionMetric label="Carousels" value={carouselCount} total={totalPosts} color="#4f46e5" />
                            <DistributionMetric label="Captions" value={captionCount} total={totalPosts} color="#8b5cf6" />
                            <DistributionMetric label="Other Content" value={otherCount} total={totalPosts} color="#d8b4fe" />
                        </Stack>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #f1f5f9' }}>
                            <Typography variant="body2" color="#64748b" fontWeight={500} sx={{ mb: 2, lineHeight: 1.6 }}>
                                You're creating <span style={{ color: '#111827', fontWeight: 700 }}>24% more</span> carousel content this month compared to last.
                            </Typography>
                            <MuiButton endIcon={<ArrowRight size={16} />} sx={{ color: '#4f46e5', fontWeight: 700, textTransform: 'none', p: 0, '&:hover': { background: 'none', textDecoration: 'underline' } }}>
                                View optimization tips
                            </MuiButton>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

function DistributionMetric({ label, value, total, color }: any) {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" fontWeight={600} color="#334155">{label}</Typography>
                <Typography variant="body2" fontWeight={700} color="#111827">{percentage.toFixed(0)}%</Typography>
            </Box>
            <Box sx={{ height: 8, width: '100%', bgcolor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${percentage}%`, bgcolor: color, borderRadius: 4 }} />
            </Box>
        </Box>
    );
}
